import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExtensionIcon from '@mui/icons-material/Extension';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { MODULOS_CATALOGO, ModuloCatalogoItem } from '../../config/modulosCatalogo';
import modulosService, { ModuloMasterDto } from '../../services/modulosService';
import ModuloCard from '../../components/modulos/ModuloCard';
import ModuloDetalleDialog from '../../components/modulos/ModuloDetalleDialog';
import { getApiErrorMessage } from '../../utils/apiError';
import { isSuperAdminRole } from '../../utils/sistemaAuthz';

type FiltroEstado = 'todos' | 'habilitados' | 'deshabilitados';

const ModulosBiblioteca: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { reload: reloadPermissions } = usePermissions();
  const esSuperAdmin = isSuperAdminRole(user?.roles);

  const [loading, setLoading] = useState(true);
  const [estados, setEstados] = useState<Record<string, boolean>>({});
  const [masterByCampo, setMasterByCampo] = useState<Record<string, ModuloMasterDto>>({});
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [selected, setSelected] = useState<ModuloCatalogoItem | null>(null);
  const [togglingCampo, setTogglingCampo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const estadosResp = await modulosService.getEstados();
      setEstados(estadosResp);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar la biblioteca de módulos'), {
        variant: 'error',
      });
    }

    try {
      const masterResp = await modulosService.getCatalogoMaster();
      const map: Record<string, ModuloMasterDto> = {};
      for (const item of masterResp) {
        map[item.campo.toUpperCase()] = item;
      }
      setMasterByCampo(map);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar el costo y el logo de los módulos'), {
          variant: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const habilitadosCount = useMemo(
    () => MODULOS_CATALOGO.filter((m) => estados[m.campo.toUpperCase()]).length,
    [estados],
  );

  const filtrados = useMemo(() => {
    const term = search.trim().toLowerCase();
    return MODULOS_CATALOGO.filter((m) => {
      const enabled = Boolean(estados[m.campo.toUpperCase()]);
      if (filtroEstado === 'habilitados' && !enabled) return false;
      if (filtroEstado === 'deshabilitados' && enabled) return false;
      if (!term) return true;
      const haystack = [
        m.nombre,
        m.descripcionCorta,
        m.descripcionLarga,
        m.categoria,
        ...m.procesos,
        ...m.entidades,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [search, filtroEstado, estados]);

  const handleToggle = async (item: ModuloCatalogoItem, habilitado: boolean) => {
    const key = item.campo.toUpperCase();
    const prev = Boolean(estados[key]);
    setEstados((s) => ({ ...s, [key]: habilitado }));
    setTogglingCampo(key);
    try {
      const result = await modulosService.setHabilitado(item.campo, habilitado);
      await reloadPermissions();
      const partes = [
        habilitado ? `${item.nombre} habilitado` : `${item.nombre} deshabilitado`,
      ];
      if (result.motivosCreados.length > 0) {
        partes.push(`Se crearon los motivos: ${result.motivosCreados.join(', ')}`);
      }
      if (result.motivosEliminados.length > 0) {
        partes.push(`Se eliminaron los motivos: ${result.motivosEliminados.join(', ')}`);
      }
      if (result.motivosConservados.length > 0) {
        partes.push(
          `Se conservaron los motivos (tienen movimientos): ${result.motivosConservados.join(', ')}`,
        );
      }
      enqueueSnackbar(partes.join('. '), { variant: 'success' });
    } catch (error) {
      setEstados((s) => ({ ...s, [key]: prev }));
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo actualizar el módulo'), {
        variant: 'error',
      });
    } finally {
      setTogglingCampo(null);
    }
  };

  const handleMasterChange = (next: ModuloMasterDto) => {
    setMasterByCampo((prev) => ({
      ...prev,
      [next.campo.toUpperCase()]: next,
    }));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'flex-start' }}
              spacing={1.5}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ExtensionIcon sx={{ color: '#1976d2' }} />
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Módulos
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Biblioteca de funcionalidades opcionales. Habilitá o deshabilitá módulos según el
                  plan de la empresa.
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {habilitadosCount} de {MODULOS_CATALOGO.length} habilitados
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por nombre, descripción, procesos o entidades…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={filtroEstado}
                onChange={(_, value: FiltroEstado | null) => {
                  if (value) setFiltroEstado(value);
                }}
                sx={{ flexShrink: 0 }}
              >
                <ToggleButton value="todos">Todos</ToggleButton>
                <ToggleButton value="habilitados">Habilitados</ToggleButton>
                <ToggleButton value="deshabilitados">Deshabilitados</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {loading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))}
              </Grid>
            ) : filtrados.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">No hay módulos que coincidan con la búsqueda.</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filtrados.map((item) => {
                  const key = item.campo.toUpperCase();
                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.campo}>
                      <ModuloCard
                        item={item}
                        habilitado={Boolean(estados[key])}
                        master={masterByCampo[key]}
                        mostrarCosto={esSuperAdmin}
                        toggling={togglingCampo === key}
                        onToggle={(hab) => handleToggle(item, hab)}
                        onOpen={() => setSelected(item)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}

            <Typography variant="caption" color="text.secondary">
              Los parámetros generales del sistema siguen en{' '}
              <Link component={RouterLink} to="/configuraciones/parametros" underline="hover">
                Parámetros del sistema
              </Link>
              .
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <ModuloDetalleDialog
        open={Boolean(selected)}
        item={selected}
        habilitado={selected ? Boolean(estados[selected.campo.toUpperCase()]) : false}
        master={selected ? masterByCampo[selected.campo.toUpperCase()] : undefined}
        roles={user?.roles}
        toggling={selected ? togglingCampo === selected.campo.toUpperCase() : false}
        onClose={() => setSelected(null)}
        onToggle={(hab) => {
          if (selected) handleToggle(selected, hab);
        }}
        onMasterChange={handleMasterChange}
      />
    </Box>
  );
};

export default ModulosBiblioteca;
